import {TextStyle} from 'react-native';

import {lineHeight, resolveWeight, type} from '../../design-system/tokens';

/**
 * Type styles the header needs and `type.*` lacks (DIS-1840). Each one reuses a token's size
 * and resolves its weight through `resolveWeight()`, so Android gets the right face.
 *
 * SHORTCUT: token gaps.
 * - 16/500, 12/500, 12/300: the DIS-1859 redline's tokenGaps record "new token proposal:
 *   Medium and Light weights recur in header and footer; type.* has neither at these sizes".
 * - 16/600 (dropdown main links): header/dropdown/desk 204:27672, as the header prototype
 *   (ea875fae6 chromeType.ts) carries it; type.* has no 16 SemiBold.
 *   That redline is a local DIS-1859 artifact, not a file in this repo.
 * - 15/700 (account greeting): docs/DIS-1840/artifacts/redline/account-dropdown/spec.json
 *   tokenGaps ("13 / Rubik Bold", new token proposal); raised to 15 on request (2026-10-30).
 *   Off the type scale.
 * - 26.423/700 (account banner): Figma accountdropdown-banner 475:23999, as the header
 *   prototype carries it. Off the type scale; the size stays literal.
 * Upgrade path: add the agreed styles to app/design-system/tokens/typography.ts (DIS-1735)
 * and replace each member below with the token.
 */
const size16 = type.button.category.fontSize;
const size12 = type.paragraph.p3.fontSize;
// SHORTCUT: 13, 15 and 9 are off the type scale (header/mob 65:4145 store strap and search
// placeholder; 229:6121 cart badge). The strap and badge draw line height = size (Figma 100%);
// `lineHeight` tokens carry no 100% style. Upgrade path: design moves them to 12 / 14 / 11, or
// DIS-1735 adds them to typography.ts.
const size13 = 13;
const size15 = 15;
const size9 = 9;
// Menu sheet (DIS-1898 B9, redline mobile-menu-sheet token-sheet.md, header/dropdown/mob
// 470:20199). Sizes 20 and 18 are on the scale (type.heading.h1, type.paragraph.p1).
// SHORTCUT: token gaps "Text 20/600", "Text 18/500, 18/600": type.* has no SemiBold 20 or
// Medium / SemiBold 18, and Figma draws line height 100%, which `lineHeight` does not carry.
// Figma's letter spacing -0.25 on 18/600 is dropped: no letterSpacing token exists (escalate,
// not improvise). Upgrade path: DIS-1735 adds these styles to typography.ts.
const size20 = type.heading.h1.fontSize as number;
const size18 = type.paragraph.p1.fontSize as number;

export const chromeType = Object.freeze({
  /** Nav labels. Figma draws line height 1.0, as Button/Category. */
  label16Medium: {
    fontSize: size16,
    ...resolveWeight('medium'),
    ...lineHeight.button.category,
  } as TextStyle,
  /** Dropdown main links ("Shop All Wine"). Line height 1.0, as Button/Category. */
  label16SemiBold: {
    fontSize: size16,
    ...resolveWeight('semiBold'),
    ...lineHeight.button.category,
  } as TextStyle,
  /** Strap store name, account dropdown items. */
  label12Medium: {fontSize: size12, ...resolveWeight('medium')} as TextStyle,
  /** Strap availability line. */
  body12Light: {fontSize: size12, ...resolveWeight('light')} as TextStyle,
  /** Mobile strap store name. */
  label13Medium: {fontSize: size13, lineHeight: size13, ...resolveWeight('medium')} as TextStyle,
  /** Mobile strap availability line. */
  body13Light: {fontSize: size13, lineHeight: size13, ...resolveWeight('light')} as TextStyle,
  /** Mobile search field placeholder. */
  body15Regular: {fontSize: size15, ...resolveWeight('regular')} as TextStyle,
  /** Menu sheet title row ("Shop All", submenu title). */
  title20SemiBold: {
    fontSize: size20,
    lineHeight: size20,
    ...resolveWeight('semiBold'),
  } as TextStyle,
  /** Menu sheet category rows. */
  label18SemiBold: {
    fontSize: size18,
    lineHeight: size18,
    ...resolveWeight('semiBold'),
  } as TextStyle,
  /** Menu sheet main links, group headings and "Shop All" links. */
  label18Medium: {fontSize: size18, lineHeight: size18, ...resolveWeight('medium')} as TextStyle,
  /** Menu sheet group items and drawer entries: type.paragraph.p1 at Figma's 100% line height. */
  body18Regular: {...type.paragraph.p1, lineHeight: size18} as TextStyle,
  /** Menu sheet store card name (15 is off the scale, as size15 above). */
  label15Medium: {fontSize: size15, lineHeight: size15, ...resolveWeight('medium')} as TextStyle,
  /** Menu sheet store card availability line. */
  body15Light: {fontSize: size15, lineHeight: size15, ...resolveWeight('light')} as TextStyle,
  /** Mobile and desktop cart badge count. */
  badge9Regular: {fontSize: size9, lineHeight: size9, ...resolveWeight('regular')} as TextStyle,
  /** Account dropdown greeting (15 is off the scale, as size15 above; was 13 in Figma). */
  label15Bold: {fontSize: size15, ...resolveWeight('bold')} as TextStyle,
  // SHORTCUT: token gap "14 / Medium" (mobile account panel rows, IhAQZ4QmxWw2P3ZfjXAkEn
  // 256:10489): type.paragraph.p2 is 14 Regular, and Figma draws line height 100%, which
  // `lineHeight` does not carry. Upgrade path: DIS-1735 adds the style to typography.ts.
  /** Mobile account panel rows. */
  label14Medium: {
    fontSize: type.paragraph.p2.fontSize,
    lineHeight: type.paragraph.p2.fontSize,
    ...resolveWeight('medium'),
  } as TextStyle,
  // SHORTCUT: 26.423 is off the type scale (accountdropdown-banner 475:23999, Rubik Bold,
  // line height 1); type.heading.h1 is 20. Upgrade path: design moves it onto the scale, or
  // DIS-1735 adds a display size to typography.ts.
  /** Account dropdown banner headline. */
  banner26Bold: {fontSize: 26.423, lineHeight: 26.423, ...resolveWeight('bold')} as TextStyle,
});
